import { useCallback, useState } from 'react';
import { useApiFetch } from '../api';
import type { DomainTransferabilityResult } from '../types/models/DomainTransferability';
import type { ApiResponseDomainTransferability } from '../types/api/ApiResponseDomainTransferability';

interface UseDomainTransferabilityReturn {
  results: DomainTransferabilityResult[];
  loading: boolean;
  error: string | null;
  /** True once at least one check has completed — distinguishes "no results" from "not checked yet". */
  hasChecked: boolean;
  /**
   * Lazy transferability check for one or more FQDNs (≤25). The auth code is **never** sent here —
   * this endpoint is a tenant-relative pre-check only. Resolves the fetched results (also stored on
   * `results`), or `[]` on error/empty input.
   */
  check: (domains: string[]) => Promise<DomainTransferabilityResult[]>;
  /** Clears results/error so the panel returns to its pristine state. */
  reset: () => void;
}

/**
 * `GET /datacenters/domain-names/transferability?domains=<csv>` — a **lazy** hook (no fetch on
 * mount). Call `check()` from a button. JSend errors arrive at HTTP 200 with `status: 'error'`
 * (the "no valid candidate" field error under `data.domains`), so success is `body.status ===
 * 'success'` plus a `results` array — never `response.ok`.
 */
export const useDomainTransferability = (): UseDomainTransferabilityReturn => {
  const apiFetch = useApiFetch();
  const [results, setResults] = useState<DomainTransferabilityResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasChecked, setHasChecked] = useState(false);

  const check = useCallback(
    async (domains: string[]): Promise<DomainTransferabilityResult[]> => {
      const cleaned = domains.map((d) => d.trim()).filter((d) => d !== '');
      if (cleaned.length === 0) {
        setError('Enter a domain name to check.');
        return [];
      }
      setLoading(true);
      setError(null);
      try {
        const params = new URLSearchParams({ domains: cleaned.join(',') });
        const response = await apiFetch(`/datacenters/domain-names/transferability?${params.toString()}`);
        const body = (await response.json().catch(() => null)) as ApiResponseDomainTransferability | null;

        if (body?.status === 'success' && Array.isArray(body.data?.results)) {
          setResults(body.data.results);
          setHasChecked(true);
          return body.data.results;
        }

        const fieldError = typeof body?.data?.domains === 'string' ? body.data.domains : null;
        setError(fieldError ?? body?.message ?? 'The transferability check failed. Please try again.');
        return [];
      } catch (err) {
        setError(err instanceof Error ? err.message : 'An unexpected error occurred while checking transferability.');
        return [];
      } finally {
        setLoading(false);
      }
    },
    [apiFetch],
  );

  const reset = useCallback(() => {
    setResults([]);
    setError(null);
    setHasChecked(false);
  }, []);

  return { results, loading, error, hasChecked, check, reset };
};
